import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Button } from 'primeng/button';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioFormBase } from '@/shared/components/relatorios/relatorio-form.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { ComposicaoOrgaoService } from '@/core/api/sicom-api/registro/orgao/relatorios/composicao/composicao-orgao.service';
import { ComposicaoOrgaoRequest, ComposicaoOrgaoResponse } from '@/core/api/sicom-api/registro/orgao/relatorios/composicao/composicao-orgao.model';
import { ComposicaoOrgaoJobService } from '@/core/services/composicao-orgao-job.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

type Req = ComposicaoOrgaoRequest;
type Resp = ComposicaoOrgaoResponse;

interface ComposicaoOrgaoFormControls {
    uf: FormControl<string | null>;
}

/**
 * Relatório PDF assíncrono da Composição Efetiva do órgão/delegacia (1 conselho/delegacia por UF).
 * Herda `RelatorioFormBase`: export/polling/download/erro vêm da base; aqui só resolvemos a UF
 * (o backend resolve o `idSolicitante` a partir dela, mesma regra do dashboard-orgao).
 */
@Component({
    selector: 'app-relatorio-composicao',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, ReactiveFormsModule, SelectEstado, RelatorioContainer, CanDirective],
    templateUrl: './relatorio-composicao.html',
    styleUrl: './relatorio-composicao.scss'
})
export class RelatorioComposicao extends RelatorioFormBase<Req, Resp> {
    /** Gate do botao Gerar: o backend exige esta role em /reports/** do modulo. */
    protected override readonly roleGerar = PERMISSOES.orgaos.relatorios.gerar;

    private readonly relatorioService = inject(ComposicaoOrgaoService);
    readonly jobService = inject(ComposicaoOrgaoJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.jobService.job$;

    readonly form = new FormGroup<ComposicaoOrgaoFormControls>({
        uf: new FormControl<string | null>(null, Validators.required)
    });

    get gerarBloqueado(): boolean {
        return this.isLoading() || this.isPending || this.form.invalid;
    }

    protected get jobFacade(): RelatorioJobFacade {
        return this.jobService;
    }

    /** Monta o request a partir da UF selecionada; retorna null (marcando o campo) se vazia. */
    protected buildRequest(): Req | null {
        this.form.markAllAsTouched();
        if (this.gerarBloqueado) return null;
        return { uf: this.form.getRawValue().uf as string };
    }

    protected exportar(request: Req) {
        return this.relatorioService.exportarPdf(request);
    }

    changeUf(uf: string | null): void {
        this.form.get('uf')?.setValue(uf ?? null);
        this.ufTocada = true;
    }

    override marcarUfTocada(): void {
        this.ufTocada = true;
        this.form.get('uf')?.markAsTouched();
    }

    limparFormulario(): void {
        this.ufTocada = false;
        this.form.reset({ uf: null });
        this.limparJob();
    }
}
